/**
 * HouseholdMembersCard — the adults on a household, on `/my-family`.
 *
 * NAMING TRAP: the card is titled "Guardians" — the ADULTS on the household,
 * `Account.ownerUserId` plus `HouseholdAccess` rows. It is NOT the Prisma
 * `AccountMember` model, which is the STUDENTS on the household and is rendered
 * by the separate "Students" card. Don't cross the two.
 *
 * One row per adult: avatar, a two-line identity block (name + role tag, then
 * email), and a trailing pencil. The whole row is a single button that opens
 * `<EditMemberSheet />` directly. "+ Add guardian" opens the invite sheet
 * (`<InviteCoParentSheet />`) directly, which asks for a role using the same
 * picker.
 *
 * Every affordance is gated on the viewer's server-resolved permissions
 * (MANAGE_MEMBERS to invite/edit), never on `isOwner`.
 *
 * There is no "transfer ownership" here on purpose. Moving the billing anchor
 * is a support action in Customers ("Make billing anchor", PROD-975); the old
 * co-parent card and transfer sheet were unmounted and have been deleted.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { useDestructiveConfirm } from '@ordo/ui/destructive-confirm'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { Mail, Pencil, Plus, X } from 'lucide-react'
import { useState } from 'react'

import {
  type CoOwnerRow,
  type PendingInviteRow,
  useAccountCoOwnersQuery,
  useCancelCoOwnerInviteMutation,
  useResendCoOwnerInviteMutation,
} from '@/api/co-owners'
import { ApiError } from '@/api/http'
import { UserAvatar } from '@/components/user-identity-card'
import { COMPACT_TAG_CLASS } from '@/lib/tag-classes'

import { EditMemberSheet } from './edit-member-sheet'
import { getDisplayName, getInitials, getPendingInviteStatusMeta, getShortDisplayName, getSignInLabel } from './helpers'
import { HOUSEHOLD_ROLE_TAG_LABEL } from './household-roles'
import { InviteCoParentSheet } from './invite-co-parent-sheet'

export interface HouseholdMembersCardProps {
  accountId: string
  /** The currently-signed-in User's id — used for "(you)" labels. */
  viewerUserId: string
}

export function HouseholdMembersCard({ accountId, viewerUserId }: HouseholdMembersCardProps) {
  const { data, isPending, error, refetch } = useAccountCoOwnersQuery(accountId)
  const resendInviteMutation = useResendCoOwnerInviteMutation(accountId)
  const cancelInviteMutation = useCancelCoOwnerInviteMutation(accountId)
  const confirm = useDestructiveConfirm()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)

  const members = data?.coOwners ?? []
  const pendingInvites = data?.pendingInvites ?? []
  const viewerPermissions = data?.viewer.permissions ?? []
  const roleCatalog = data?.roleCatalog ?? []
  const canManageMembers = viewerPermissions.includes('MANAGE_MEMBERS')
  // Primary guardians first (there can be several), then everyone else in the
  // order the server returned. Sorting on the ROLE, not the billing-anchor flag.
  const ordered = [...members].sort(
    (a, b) => Number(b.role === 'PRIMARY_GUARDIAN') - Number(a.role === 'PRIMARY_GUARDIAN'),
  )
  const editingMember = ordered.find((row) => row.userId === editingUserId) ?? null
  // Drives the "can't demote the last one" guard in the edit sheet.
  const primaryGuardianCount = members.filter((row) => row.role === 'PRIMARY_GUARDIAN').length

  async function handleResendInvite(invite: PendingInviteRow) {
    try {
      await resendInviteMutation.mutateAsync(invite.id)
      toast.success('Invite resent', { description: `A fresh invite is on its way to ${invite.email}.` })
    } catch (err) {
      if (err instanceof ApiError && err.code === 'cooldown') {
        const liftsAt = (err.body as { cooldownLiftsAt?: string }).cooldownLiftsAt
        toast.error('Recently sent', {
          description: liftsAt
            ? `This invite went out in the last 24 hours — try again after ${formatCooldownLift(liftsAt)}.`
            : err.message,
        })
        return
      }
      toast.error("Couldn't resend invite", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  async function handleCancelInvite(invite: PendingInviteRow) {
    const confirmed = await confirm({
      title: `Cancel invite to ${invite.email}?`,
      body: "They won't be able to join with the link they received. You can invite them again later.",
      confirmLabel: 'Cancel invite',
    })
    if (!confirmed) {
      return
    }
    try {
      await cancelInviteMutation.mutateAsync(invite.id)
      toast.success('Invite cancelled', { description: `${invite.email} can no longer use that invite link.` })
    } catch (err) {
      toast.error("Couldn't cancel invite", {
        description: err instanceof Error ? err.message : 'Please try again.',
      })
    }
  }

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Guardians</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-1.5" role="list">
            {[0, 1].map((i) => (
              // Mirrors MemberRow block-for-block: avatar, a two-line identity
              // block (name + tag, then email), and the trailing pencil.
              <li key={i} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-44" />
                </div>
                <Skeleton className="h-3.5 w-3.5 shrink-0" />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Guardians</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Couldn&apos;t load guardians: {error.message}</p>
          <Button type="button" variant="outline" size="xs" className="mt-3" onClick={() => refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          {/* text-base pins the title one step above body text rather than
              inheriting whatever the page sets. */}
          <CardTitle className="text-base">Guardians</CardTitle>
          {/* CardDescription, not a raw <p>: CardHeader already owns the
              title↔description gap via space-y-1.5, so the old `mt-1` was
              fighting it. text-xs + leading-relaxed because this wraps to two
              lines on a phone. */}
          <CardDescription className="text-xs leading-relaxed">
            The adults who manage this household and student information.
          </CardDescription>
        </CardHeader>
        {/* Three deliberate spacing levels instead of one gap-2 waterfall:
            0.5 within a row, 1.5 between rows, 5 between sections. */}
        <CardContent className="flex flex-col gap-5">
          <ul className="flex flex-col gap-1.5" role="list">
            {ordered.map((row) => (
              <MemberRow
                key={row.userId}
                row={row}
                isSelf={row.userId === viewerUserId}
                canEdit={canManageMembers || row.userId === viewerUserId}
                onEdit={() => setEditingUserId(row.userId)}
              />
            ))}
          </ul>

          {pendingInvites.length > 0 ? (
            // border-t sits centered in its whitespace: 20px above (the parent
            // gap-5) and pt-5 = 20px below.
            <div className="border-t pt-5">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Pending invites</p>
              <ul className="flex flex-col gap-1.5" role="list">
                {pendingInvites.map((invite) => {
                  const status = getPendingInviteStatusMeta(invite)
                  const actionsBusy = resendInviteMutation.isPending || cancelInviteMutation.isPending
                  return (
                    // Same three-column shape as MemberRow: icon / two-line
                    // identity / trailing actions centered across the row.
                    <li key={invite.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                      {/* 32px to match the member avatars — these rows sit in the
                          same list and must share one rhythm. */}
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-muted/30">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          {/* The email IS the identity here (no name yet), so it
                              keeps the anchor treatment a member's name gets. */}
                          <p className="min-w-0 truncate text-sm font-medium text-foreground" title={invite.email}>
                            {invite.email}
                          </p>
                          {status.isProblem ? (
                            // `isProblem` is the tone driver the helper documents;
                            // there is no `variant` on PendingInviteStatusMeta.
                            <Badge variant="warning" className={COMPACT_TAG_CLASS}>
                              {status.label}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="truncate text-xs text-muted-foreground">
                          Invited {formatInviteTimestamp(invite.createdAt)}
                          {status.isProblem && status.detail ? ` · ${status.detail}` : ''}
                        </p>
                      </div>
                      {canManageMembers ? (
                        // size="xs"/"icon-xs" (h-6) — a compact row needs compact
                        // controls; the default h-9 button set the row's height.
                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            disabled={actionsBusy}
                            onClick={() => {
                              void handleResendInvite(invite)
                            }}>
                            Resend
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            aria-label={`Cancel invite to ${invite.email}`}
                            disabled={actionsBusy}
                            onClick={() => {
                              void handleCancelInvite(invite)
                            }}>
                            <X className="h-3 w-3" aria-hidden />
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </div>
          ) : null}

          {canManageMembers ? (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/30 hover:text-foreground">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add guardian
            </button>
          ) : null}
        </CardContent>
      </Card>

      <InviteCoParentSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        accountId={accountId}
        roleCatalog={roleCatalog}
        canAssignOwner={viewerPermissions.includes('MANAGE_PRIMARY_GUARDIAN')}
        onInvited={() => setInviteOpen(false)}
      />

      <EditMemberSheet
        open={editingMember !== null}
        onOpenChange={(next) => {
          if (!next) {
            setEditingUserId(null)
          }
        }}
        accountId={accountId}
        member={editingMember}
        viewerUserId={viewerUserId}
        viewerPermissions={viewerPermissions}
        roleCatalog={roleCatalog}
        primaryGuardianCount={primaryGuardianCount}
      />
    </>
  )
}

function MemberRow({
  row,
  isSelf,
  canEdit,
  onEdit,
}: {
  row: CoOwnerRow
  isSelf: boolean
  canEdit: boolean
  onEdit: () => void
}) {
  const displayName = getDisplayName(row.user)
  const shortName = getShortDisplayName(row.user)
  const signInLabel = getSignInLabel(row.user)
  const isPrimary = row.role === 'PRIMARY_GUARDIAN'

  // Three columns, not four: avatar / two-line identity / trailing affordance.
  // The pencil is a ROW-level element, vertically centered across both lines —
  // parked on the email's line it read as "edit this email", and it stole width
  // from the one thing that needs it.
  const content = (
    <>
      <UserAvatar
        name={displayName}
        initials={getInitials(row.user)}
        avatarUri={row.user.avatarUri}
        avatarColor={row.user.avatarColor}
        // `size="md"` is 32px and is the size the fallback initials are tuned
        // for. The old `h-10 w-10` override pushed it to 40px, so the circle
        // outweighed the name it belongs to and left the initials floating.
        size="md"
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-1.5">
          {/* "Dana W." on a phone, the full name from `sm` up — the surname is what
              crowds the tag out at 375px. Both spellings render; CSS picks one, so
              there is no viewport-width guess in JS and no hydration mismatch.
              `title` always carries the full name. Truncation stays as the
              backstop for a long first name. */}
          <p className="min-w-0 truncate text-sm font-medium text-foreground" title={displayName}>
            <span className="sm:hidden">{shortName}</span>
            <span className="hidden sm:inline">{displayName}</span>
            {isSelf ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">(you)</span> : null}
          </p>
          {/* Always `secondary` as the base: it carries no `dark:` classes, so the
              tint below fully controls both themes. A variant like `info` would
              leak its own `dark:text-*` past a plain `text-*` override. */}
          <Badge
            variant="secondary"
            className={cn(
              COMPACT_TAG_CLASS,
              isPrimary ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
            )}>
            {row.role ? HOUSEHOLD_ROLE_TAG_LABEL[row.role] : 'Guardian'}
          </Badge>
        </div>
        {/* A full step down from the name, not merely muted at the same size —
            the address is an identifier you confirm, never something you scan.
            Dropping to text-xs also fits ~8 more characters before truncating. */}
        <p className="truncate text-xs text-muted-foreground" title={signInLabel}>
          {signInLabel}
        </p>
      </div>
      {canEdit ? (
        // A bare icon, NOT a button: the whole row is already the button, and
        // nesting one inside another is invalid and breaks keyboard focus. Hence
        // aria-hidden — the row's own aria-label names the action.
        <Pencil
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
          aria-hidden
        />
      ) : null}
    </>
  )

  if (!canEdit) {
    return <li className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">{content}</li>
  }

  // The whole row opens the edit panel — one tap, no dropdown in between. The
  // pencil is the affordance, not a second target.
  return (
    <li>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${displayName}`}
        className="group flex w-full items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:border-foreground/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
        {content}
      </button>
    </li>
  )
}

function formatInviteTimestamp(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
}

function formatCooldownLift(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
